import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { openContactForm } from '../../store/slices/uiSlice';
import {
  BellRing,
  ArrowRight,
  Heart,
  ShieldCheck,
} from 'lucide-react';


const FOOTER_LINK_CLASS = 'text-slate-500 hover:text-indigo-600 transition-colors flex items-center gap-1.5';

// Site pages route in-app, the contact entry opens the contact form, and the
// rest are still in-page anchors
const FooterLink = ({ link }) => {
  const dispatch = useDispatch();
  const content = (
    <>
      <span>{link.label}</span>
      {link.badge && (
        <span className="text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.2 rounded">
          {link.badge}
        </span>
      )}
    </>
  );
  if (link.action === 'contact') {
    return (
      <button type="button" onClick={() => dispatch(openContactForm())} className={FOOTER_LINK_CLASS}>
        {content}
      </button>
    );
  }
  return link.href.startsWith('/') ? (
    <Link to={link.href} className={FOOTER_LINK_CLASS}>
      {content}
    </Link>
  ) : (
    <a href={link.href} className={FOOTER_LINK_CLASS}>
      {content}
    </a>
  );
};

export const Footer = () => {
  const [subscribeInput, setSubscribeInput] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (subscribeInput.trim()) {
      setSubscribed(true);
      setSubscribeInput('');
    }
  };

  const footerSections = [
    {
      title: 'IPOS & ISSUES',
      links: [
        { label: 'Mainboard IPOs', href: '#mainboard' },
        { label: 'SME IPOs (NSE/BSE Emerge)', href: '#sme' },
        { label: 'Current & Active IPOs', href: '#active' },
        { label: 'Upcoming IPO Pipeline', href: '#pipeline' },
        { label: 'Recently Closed IPOs', href: '#closed' },
        { label: 'IPO Calendar 2026', href: '#calendar' },
        { label: 'Grey Market Premium (GMP)', badge: 'Live', href: '#gmp' },
      ],
    },
    {
      title: 'TOOLS & CALCULATORS',
      links: [
        // { label: 'IPO Allotment Status Checker', href: '#allotment-check' },
        { label: 'Live Subscription Tracker', href: '#sub-tracker' },
        // { label: 'Listing Gain & Profit Calculator', href: '#profit-calc' },
        // { label: 'Basis of Allotment Analyzer', href: '#basis' },
        // { label: 'Multi-Account Bid Tracker', href: '#multi-bid' },
        { label: 'ASBA & UPI Mandate Guide', href: '#asba-guide' },
        { label: 'IPO Form Download', href: '#form-download' },
      ],
    },
    {
      title: 'RESEARCH & NEWS',
      links: [
        { label: 'IPO Analysis & Reviews', href: '#analysis' },
        { label: 'DRHP & RHP Filing Tracker', href: '#drhp-tracker' },
        { label: 'Primary Market News', href: '#market-news' },
        // { label: 'Anchor Investor Allotments', href: '#anchor' },
        { label: 'SEBI Approval Status Tracker', href: '/sebi-filings-approvals' },
        // { label: 'Historical IPO Performance', href: '#historical' },
      ],
    },
    {
      title: 'COMPANY & LEGAL',
      links: [
        { label: 'About Alambrix IPO', href: '#about' },
        { label: 'Contact & Support', action: 'contact' },
        // { label: 'National & SEBI Compliance', href: '#compliance' },
        // { label: 'Disclaimer & Risk Warning', href: '#disclaimer' },
        { label: 'Terms of Service', href: '#terms' },
        { label: 'Privacy Policy', href: '#privacy' },
        // { label: 'Advertise with Us', href: '#advertise' },
      ],
    },
  ];

  return (
    <footer className="bg-slate-50 border-t border-slate-200/80 pt-12 sm:pt-16 pb-12 text-slate-600 text-xs sm:text-sm">
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28">

        {/* Top Card: Brand Overview + Alert Box */}
        <div className="bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 lg:p-10 shadow-xs mb-12 sm:mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">

            {/* Left: Brand + Description + Socials */}
            <div className="lg:col-span-7 space-y-4">
              <div className="flex items-center gap-2.5">
                {/* Logo Icon */}
                <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-[#006666] via-[#008080] to-[#2dd4bf] flex items-center justify-center text-white shadow-md shadow-[#008080]/30 ring-1 ring-white/30 flex-shrink-0">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 3.5L4 19.5H8.5L12 12L15.5 19.5H20L12 3.5Z" fill="white" />
                    <path d="M7.8 15H16.2L14.2 19H9.8L7.8 15Z" fill="#ccfbf1" />
                    <circle cx="12" cy="7" r="1.2" fill="#008080" />
                  </svg>
                </div>
                <div className="flex items-center tracking-tight">
                  <span className="font-extrabold text-xl text-slate-900">Alambrix</span>
                  <span className="ml-1 text-xs font-bold text-[#008080] bg-teal-50 border border-teal-100 px-1.5 py-0.5 rounded uppercase tracking-wider">
                    IPO
                  </span>
                </div>
              </div>

              <p className="text-slate-500 text-xs sm:text-sm leading-relaxed max-w-2xl font-normal">
                India’s leading IPO tracking, Grey Market Premium (GMP) analysis, and live subscription intelligence platform. Empowering retail investors with institutional-grade primary market intelligence.
              </p>

              {/* Social Media Links */}
              <div className="flex items-center gap-3 pt-2">
                <a
                  href="#twitter"
                  className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 hover:bg-teal-50 hover:text-teal-700 flex items-center justify-center transition-colors"
                  aria-label="X (Twitter)"
                >
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a
                  href="#linkedin"
                  className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 hover:bg-teal-50 hover:text-teal-700 flex items-center justify-center transition-colors"
                  aria-label="LinkedIn"
                >
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                  </svg>
                </a>
                <a
                  href="#youtube"
                  className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 hover:bg-teal-50 hover:text-teal-700 flex items-center justify-center transition-colors"
                  aria-label="YouTube"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
                <a
                  href="#instagram"
                  className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 hover:bg-teal-50 hover:text-teal-700 flex items-center justify-center transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Right: Newsletter / WhatsApp Alert Box */}
            <div className="lg:col-span-5 bg-gradient-to-br from-teal-50/80 via-white to-slate-50 border border-teal-100/90 rounded-2xl p-5 sm:p-6 shadow-xs">
              <div className="flex items-center gap-2 text-teal-800 font-bold text-sm sm:text-base">
                <BellRing className="w-4 h-4 text-[#008080] animate-bounce" />
                <span>Get Daily IPO GMP & Subscription Alerts</span>
              </div>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Receive instant alerts directly on your WhatsApp or inbox every morning before market opening.
              </p>

              {subscribed ? (
                <div className="mt-4 p-3 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-semibold flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  <span>Subscribed successfully! You will receive daily morning market alerts.</span>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="mt-4 flex items-center gap-2">
                  <input
                    type="text"
                    required
                    value={subscribeInput}
                    onChange={(e) => setSubscribeInput(e.target.value)}
                    placeholder="Enter WhatsApp number or Email..."
                    className="flex-1 py-2 px-3.5 text-xs sm:text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-slate-400"
                  />
                  <button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold text-xs sm:text-sm px-4 py-2 rounded-xl transition-all shadow-sm flex items-center gap-1 flex-shrink-0"
                  >
                    <span>Subscribe</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>

        {/* 4 Navigation Link Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-10 pb-12 border-b border-slate-200/80 text-xs">
          {footerSections.map((section) => (
            <div key={section.title} className="space-y-3">
              <h4 className="font-bold text-slate-900 tracking-wider text-[11px] uppercase">
                {section.title}
              </h4>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <FooterLink link={link} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Regulatory Disclaimer & Copyright */}
        <div className="pt-8 space-y-4 text-[11px] text-slate-400 leading-relaxed">
          <p>
            <strong className="text-slate-600 font-semibold">Statutory Regulatory Disclaimer:</strong> Alambrix IPO is an independent primary market news aggregator, educational resource, and analytical journal. We are NOT registered with the Securities and Exchange Board of India (SEBI) as an investment advisor or research analyst. The Grey Market Premium (GMP) data displayed on this portal represents unconfirmed, unregulated secondary market bid quotes gathered solely for educational reference and should never be used as sole criteria for investment decisions. Investing in IPOs carries market risk. Read the Offer Documents (DRHP/RHP) filed with SEBI and consult your certified financial planner before applying.
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2 border-t border-slate-200/60 text-slate-500">
            <div>
              © 2024 Alambrix Technologies Pvt. Ltd. All rights reserved. Data sourced from NSE, BSE & registered registrars.
            </div>
            <div className="flex items-center gap-1 text-slate-400">
              <span>Crafted with</span>
              <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-500 inline" />
              <span>for Indian Retail Investors</span>
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
